import {
  IAnsweredItem,
  IAnsweredShippingAddress,
  IQuotationAnswer,
  IRenegotiateQuotationAnswer,
} from '@/types';

export function adaptAnswerToRenegotiate(
  answer: IQuotationAnswer
): IRenegotiateQuotationAnswer {
  return {
    merged_answered_items: answer.merged_answered_items,
    answered_shipping_addresses: answer.answered_shipping_addresses,
    arrival_estimate: answer.arrival_estimate,
    cnpj: answer.cnpj,
    cpf: answer.cpf,
    installments: answer.installments,
    installments_observations: answer.installments_observations,
    observations: answer.observations,
    payment_method: answer.payment_method,
    supplier_foreign_id: answer.supplier_foreign_id,
    valid_until: answer.valid_until,
  };
}

export function renegotiationHasAnyDifference(
  renegotiation: IRenegotiateQuotationAnswer,
  quotationAnswer: IQuotationAnswer
) {
  const hasDifferenceInItems = renegotiation.merged_answered_items.some(
    ({ answered_items }) =>
      answered_items.some(newAnsweredItem => {
        const oldAnsweredItem = quotationAnswer.answered_items.find(
          ({ id }) => id === newAnsweredItem.id
        );

        if (!oldAnsweredItem) return false;
        return !compareAnsweredItems(newAnsweredItem, oldAnsweredItem);
      })
  );

  const hasDifferenceInShippingAddresses =
    renegotiation.answered_shipping_addresses.some(newShippingAddress => {
      const oldShippingAddress =
        quotationAnswer.answered_shipping_addresses.find(
          ({ id }) => id === newShippingAddress.id
        );

      if (!oldShippingAddress) return false;
      return !compareShippingAddress(newShippingAddress, oldShippingAddress);
    });

  const hasDifferenceInPayment =
    renegotiation.payment_method.id !== quotationAnswer.payment_method.id;

  const hasDifferenceInstallments =
    renegotiation.installments !== quotationAnswer.installments;

  const hasDifferenceInArrivalEstimate =
    renegotiation.arrival_estimate !== quotationAnswer.arrival_estimate;

  const hasDifferenceInValidUntil =
    renegotiation.valid_until !== quotationAnswer.valid_until;

  const hasDifferenceInObservations =
    renegotiation.observations !== quotationAnswer.observations;

  return (
    hasDifferenceInItems ||
    hasDifferenceInShippingAddresses ||
    hasDifferenceInPayment ||
    hasDifferenceInstallments ||
    hasDifferenceInArrivalEstimate ||
    hasDifferenceInValidUntil ||
    hasDifferenceInObservations
  );
}

export function compareAnsweredItems<T extends IAnsweredItem>(
  newItem: T,
  oldItem: T
) {
  return (
    oldItem.available === newItem.available &&
    oldItem.unit_price_micros === newItem.unit_price_micros &&
    oldItem.total_quantity_micros === newItem.total_quantity_micros &&
    oldItem.total_discount_micros === newItem.total_discount_micros
  );
}

export function compareShippingAddress<T extends IAnsweredShippingAddress>(
  newShipping: T,
  oldShipping: T
) {
  return (
    oldShipping.total_freight_micros === newShipping.total_freight_micros &&
    oldShipping.answered === newShipping.answered &&
    oldShipping.free_shipping === newShipping.free_shipping
  );
}

export function createPayload(answer: IRenegotiateQuotationAnswer) {
  const mapAnsweredItems = (answeredItem: IAnsweredItem) => ({
    id: answeredItem.id,
    answered: answeredItem.answered,
    available: answeredItem.available,
    unit_price_micros: answeredItem.unit_price_micros,
    total_quantity_micros: answeredItem.total_quantity_micros,
    total_discount_micros: answeredItem.total_discount_micros,
  });

  const mapShippingAddress = (shippingAddress: IAnsweredShippingAddress) => ({
    id: shippingAddress.id,
    total_freight_micros: shippingAddress.total_freight_micros,
    free_shipping: shippingAddress.free_shipping,
    answered: shippingAddress.answered,
  });

  return {
    supplier_foreign_id: answer.supplier_foreign_id,
    cpf: answer.cpf,
    cnpj: answer.cnpj,
    observations: answer.observations,
    installments: answer.installments,
    installments_observations: answer.installments_observations,
    payment_method: answer.payment_method.id,
    arrival_estimate: new Date(answer.arrival_estimate).toISOString(),
    valid_until: new Date(answer.valid_until).toISOString(),
    answered_items: answer.merged_answered_items.flatMap(merged =>
      merged.answered_items.map(mapAnsweredItems)
    ),
    answered_shipping_addresses:
      answer.answered_shipping_addresses.map(mapShippingAddress),
  };
}
